/**
 * Customer Workspace Page — Phase 1
 * Unified dashboard for sold customers showing summary data and quick access links.
 */

import {
  ArrowLeftIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  TruckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { staffPortalRequestsApi } from '../../services/staffPortalRequestsApi';
import { formatDealType } from '../../utils/dealType';

interface WorkspaceSummary {
  customer: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    status: string;
  };
  latest_deal: {
    id: string;
    deal_number: string;
    deal_type: string;
    deal_status: string;
    created_at: string | null;
  } | null;
  bhph_account: {
    id: string;
    deal_number: string;
    account_status: string;
    total_balance_amount: number;
    days_past_due: number;
    payment_due_date: string | null;
  } | null;
  documents_count: number;
  crm_summary: {
    activities_count: number;
    pending_tasks_count: number;
  };
}

const CustomerWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalRequestsCount, setPortalRequestsCount] = useState(0);

  const fetchWorkspaceSummary = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<WorkspaceSummary>(`/customers/${id}/workspace-summary`);
      setSummary(response);
    } catch (err) {
      console.error('Error fetching workspace summary:', err);
      setError('Failed to load workspace summary');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWorkspaceSummary();
  }, [fetchWorkspaceSummary]);

  useEffect(() => {
    // v2 servicing Slice 11 (D6): own independent fetch, same pattern as
    // CustomerDetailPage's Activity Timeline trace.
    if (!id) return;
    staffPortalRequestsApi
      .listForCustomer(id)
      .then((rows) => setPortalRequestsCount(rows.length))
      .catch(() => setPortalRequestsCount(0));
  }, [id]);

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Customers
          </button>
        </div>
        <div className="card animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Customers
          </button>
        </div>
        <div className="card">
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCircleIcon className="h-6 w-6 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {error || 'Workspace not available'}
            </h3>
            <p className="text-gray-500">Unable to load workspace summary for this customer.</p>
          </div>
        </div>
      </div>
    );
  }

  const { customer, latest_deal, bhph_account, documents_count, crm_summary } = summary;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Customers
        </button>
        <button onClick={() => navigate(`/customers/${id}`)} className="btn-primary">
          <UserCircleIcon className="h-4 w-4 mr-2" />
          Open Customer Profile
        </button>
      </div>

      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Customer Workspace: {customer.first_name} {customer.last_name}
        </h1>
        <p className="text-gray-600 mt-1">
          Unified view of customer details, deals, accounts, and activities
        </p>
      </div>

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Card */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center">
              <UserCircleIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Customer Profile</h3>
            </div>
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                customer.status === 'ACTIVE'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {customer.status}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-700">Name</p>
              <p className="text-base text-gray-900">
                {customer.first_name} {customer.last_name}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Phone</p>
              <p className="text-base text-gray-900">{customer.phone}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Email</p>
              <p className="text-base text-gray-900">{customer.email}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => navigate(`/customers/${id}`)}
              className="w-full btn-secondary text-center"
            >
              View Full Profile
            </button>
          </div>
        </div>

        {/* Latest Deal Card */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center">
              <TruckIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Latest Deal</h3>
            </div>
          </div>
          {latest_deal ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Deal Number</p>
                <p className="text-base text-gray-900">{latest_deal.deal_number}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Type</p>
                  <p className="text-base text-gray-900">{formatDealType(latest_deal.deal_type)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      latest_deal.deal_status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {latest_deal.deal_status}
                  </span>
                </div>
              </div>
              {latest_deal.created_at && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Created</p>
                  <p className="text-base text-gray-900">
                    {new Date(latest_deal.created_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              <div className="mt-4 pt-4 border-t space-y-2">
                <button
                  onClick={() => navigate(`/deals/${latest_deal.id}`)}
                  className="w-full btn-secondary text-center"
                >
                  View Deal Details
                </button>
                <button
                  onClick={() => navigate(`/deals?customer_id=${id}`)}
                  className="w-full btn-secondary text-center"
                >
                  View All Customer Deals
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <p>No deals found for this customer</p>
              <button
                onClick={() => navigate(`/deals?customer_id=${id}`)}
                className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View Deals
              </button>
            </div>
          )}
        </div>

        {/* BHPH Account Card */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center">
              <BanknotesIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">BHPH Account</h3>
            </div>
          </div>
          {bhph_account ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Deal Number</p>
                <p className="text-base text-gray-900">{bhph_account.deal_number}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Total Balance</p>
                <p className="text-base text-gray-900">
                  ${bhph_account.total_balance_amount.toLocaleString()}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Account Status</p>
                  <p className="text-base text-gray-900">{bhph_account.account_status}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Days Past Due</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      bhph_account.days_past_due === 0
                        ? 'bg-green-100 text-green-800'
                        : bhph_account.days_past_due <= 7
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {bhph_account.days_past_due} days
                  </span>
                </div>
              </div>
              {bhph_account.payment_due_date && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Next Payment Date</p>
                  <p className="text-base text-gray-900">
                    {new Date(bhph_account.payment_due_date).toLocaleDateString()}
                  </p>
                </div>
              )}
              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={() => navigate(`/bhph/account/${bhph_account.id}`)}
                  className="w-full btn-secondary text-center"
                >
                  Open BHPH Account
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <p>No BHPH account for this customer</p>
            </div>
          )}
        </div>

        {/* Documents & CRM Activities Card */}
        <div className="card">
          <div className="card-header flex items-center">
            <ClipboardDocumentListIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Documents & Activity</h3>
          </div>
          <div className="space-y-4">
            {/* Documents Section */}
            <div className="flex items-center justify-between py-3 border-b">
              <div className="flex items-center">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Documents</p>
                  <p className="text-xs text-gray-500">{documents_count} files</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/documents?customer_id=${id}`)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View Documents
              </button>
            </div>

            {/* CRM Activities Section */}
            <div className="flex items-center justify-between py-3 border-b">
              <div className="flex items-center">
                <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Recent Activities</p>
                  <p className="text-xs text-gray-500">{crm_summary.activities_count} activities</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/crm?customer_id=${id}`)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View CRM
              </button>
            </div>

            {/* Tasks Section */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center">
                <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Open Tasks</p>
                  <p className="text-xs text-gray-500">{crm_summary.pending_tasks_count} pending</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/crm?customer_id=${id}&tab=tasks`)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View Tasks
              </button>
            </div>

            {/* Portal Requests Section (v2 servicing Slice 11, D6) */}
            <div className="flex items-center justify-between py-3 border-t">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Portal Requests</p>
                  <p className="text-xs text-gray-500">{portalRequestsCount} requests</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/bhph/portal-requests?customer_id=${id}`)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View
              </button>
            </div>

            {/* Quick Action Buttons */}
            <div className="mt-4 pt-4 border-t space-y-2">
              <button
                onClick={() => navigate(`/customers/${id}`)}
                className="w-full btn-secondary text-center"
              >
                View Customer Profile
              </button>
              <button
                onClick={() => navigate(`/documents?customer_id=${id}`)}
                className="w-full btn-secondary text-center"
              >
                Document Center
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerWorkspacePage;
